/**
 * Geo-restriction gate for the Candle webapp.
 *
 * Calls the `get-visitor-geo` edge function on first load.
 * The edge function checks the BLOCKED_COUNTRIES list in platform_settings
 * and returns { is_blocked: true } for blocked countries.
 *
 * This hook caches the result in sessionStorage for the tab lifetime
 * so it only runs once per session. Concurrent callers share one in-flight
 * request. Landing queries must not wait for this — geo is fail-open.
 */

import { useEffect, useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const GEO_FN_URL = `${SUPABASE_URL}/functions/v1/get-visitor-geo`;
export const VISITOR_GEO_SESSION_KEY = 'candle_geo_check';

interface GeoResult {
  country_code: string | null;
  is_blocked: boolean;
}

function normalizeIso2(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

function readCachedGeo(): GeoResult | null {
  try {
    const cached = sessionStorage.getItem(VISITOR_GEO_SESSION_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached) as GeoResult;
    return {
      country_code: normalizeIso2(parsed.country_code),
      is_blocked: Boolean(parsed.is_blocked),
    };
  } catch {
    return null;
  }
}

function writeCachedGeo(result: GeoResult) {
  try {
    sessionStorage.setItem(VISITOR_GEO_SESSION_KEY, JSON.stringify(result));
  } catch {
    // Ignore storage errors
  }
}

async function fetchVisitorGeo(): Promise<GeoResult> {
  if (!SUPABASE_URL) {
    return { country_code: null, is_blocked: false };
  }

  const res = await fetch(GEO_FN_URL, { method: 'GET' });
  const data = res.ok ? ((await res.json()) as GeoResult | null) : null;
  return {
    country_code: normalizeIso2(data?.country_code),
    is_blocked: Boolean(data?.is_blocked),
  };
}

let inFlightGeo: Promise<GeoResult> | null = null;

export function resolveVisitorGeo(): Promise<GeoResult> {
  const cached = readCachedGeo();
  if (cached) return Promise.resolve(cached);
  if (inFlightGeo) return inFlightGeo;

  inFlightGeo = fetchVisitorGeo()
    .then((result) => {
      writeCachedGeo(result);
      return result;
    })
    .catch(() => ({ country_code: null, is_blocked: false }))
    .finally(() => {
      inFlightGeo = null;
    });

  return inFlightGeo;
}

if (typeof window !== 'undefined' && !readCachedGeo()) {
  void resolveVisitorGeo();
}

export function useGeoBlock(): { isBlocked: boolean; isChecking: boolean } {
  const cached = typeof window !== 'undefined' ? readCachedGeo() : null;
  const [isBlocked, setIsBlocked] = useState(() => cached?.is_blocked ?? false);

  useEffect(() => {
    void resolveVisitorGeo().then((result) => {
      setIsBlocked(result.is_blocked);
    });
  }, []);

  return { isBlocked, isChecking: false };
}

/**
 * Country for public honored ranking. Uses the same server IP lookup as
 * geo-blocking (`get-visitor-geo`). Never trusts a client-supplied country id.
 * Returns the cached country immediately; updates when the lookup finishes.
 */
export function useSecureVisitorCountry(): {
  countryCode: string | null;
} {
  const [countryCode, setCountryCode] = useState<string | null>(() =>
    typeof window !== 'undefined' ? readCachedGeo()?.country_code ?? null : null,
  );

  useEffect(() => {
    let cancelled = false;
    void resolveVisitorGeo().then((result) => {
      if (!cancelled) setCountryCode(result.country_code);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { countryCode };
}
